// MEAS-02 (Phase 35, D-05): mark boot-start as the first statement in this
// entry module, before Sentry.init or the App render, so src/App.js's
// boot-fmp mark measures the boot -> first-meaningful-paint window. (ESM
// import declarations below are still hoisted/evaluated first per spec, but
// this is the earliest point in this module's own executable code.)
// Feature-detect performance so this never throws in a non-standard environment.
if (typeof performance !== 'undefined' && typeof performance.mark === 'function') {
    performance.mark('boot-start');
}

// Sentry must be initialized before ReactDOM.createRoot so error handlers
// and the Replay integration are registered before any component tree mounts.
// [CITED: sentry.io/changelog/react-19-support/]
import * as Sentry from '@sentry/react';
import { buildSentryConfig } from './src/lib/sentryConfig';

// MEAS-02 (Phase 35, D-06): local 100%-sample override for baseline capture.
// Append `?perfSampleAll=1` to the app URL during a local dev session to force
// Sentry.tracesSampleRate to 1.0 for that session only — Plan 04 (baseline
// capture) reads this same query param. Prod always stays at the committed
// 0.1 default in sentryConfig.js; this is never read from build config.
const perfSampleAllOverride =
    typeof window !== 'undefined' && window.location && typeof window.location.search === 'string'
        ? new URLSearchParams(window.location.search).get('perfSampleAll') === '1'
        : false;

Sentry.init(buildSentryConfig(
    process.env.SENTRY_AGENT_DSN,
    perfSampleAllOverride ? { tracesSampleRate: 1.0 } : undefined
));

import React from 'react';
import ReactDOM from 'react-dom/client'; // Correct import for React 18
import './index.css'; // Assuming you might have a basic CSS file
import App from './src/App'; // Import your main App component
import { ToastProvider } from './src/contexts/ToastContext';
import { BrowserRouter } from 'react-router-dom';
//import reportWebVitals from './reportWebVitals'; // For performance tracking

// React 19 error hooks: pass reactErrorHandler so Sentry captures errors at
// the root level with full component stacks, not only at component boundaries.
//
// Supplying onUncaughtError REPLACES React's default handler (reportError), and
// reactErrorHandler() only reports to Sentry. So an uncaught render error, which
// unmounts the whole app, used to leave a blank page with nothing in the console
// (the 2026-10 tasksResponsive e2e flake). The wrapper keeps the Sentry report
// unhandled (passing a callback would mark it handled) and also logs it, so dev,
// emulator, and e2e runs show it. Production builds drop console calls.
const reportUncaughtToSentry = Sentry.reactErrorHandler();
const root = ReactDOM.createRoot(document.getElementById('root'), {
    onUncaughtError: (error, errorInfo) => {
        reportUncaughtToSentry(error, errorInfo);
        console.error('Uncaught render error; the app unmounted.', error, errorInfo?.componentStack);
    },
    onCaughtError:      Sentry.reactErrorHandler(),
    onRecoverableError: Sentry.reactErrorHandler(),
});

// Render the App component into the root element
root.render(
  <React.StrictMode>
    <BrowserRouter>
      <ToastProvider>
        <App />
      </ToastProvider>
    </BrowserRouter>
  </React.StrictMode>
);

// BOOT-02 (D-07/D-08): defer the heavy Sentry integrations (browserTracing,
// replay, feedback) to after first paint. Sentry.init() above already ran
// synchronously with the minimal config, and reactErrorHandler is already
// wired into createRoot, so boot-window error capture is unaffected — only
// this extra SDK weight moves off the critical path. Feature-detect
// requestIdleCallback with a setTimeout fallback (Safari, RESEARCH.md
// Pitfall 4); never let a deferred-integration failure throw or block the
// already-rendered app (RESEARCH.md Pattern 3).
const scheduleIdle =
    typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function'
        ? window.requestIdleCallback
        : (fn) => setTimeout(fn, 1);

scheduleIdle(() => {
    import('./src/lib/sentryDeferredIntegrations')
        .then(({ addHeavyIntegrations }) => addHeavyIntegrations(Sentry))
        .catch(() => { /* never break boot on a deferred-integration failure */ });
});

//reportWebVitals();